import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { DepositLedgerTableRecord, LedgerAccountType } from '@mews-commander/data-access';
import { EnterpriseLink, LocalizedDateFromUtc, TruncatedText } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { Typography } from '@optimus-web/core';
import { LocalizedText, LocalizedCurrencyValue } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ReservationStatusTag } from '../../common/ReservationStatusTag';
import { BillColumn } from '../../common/columns';
import { TEXT_LENGTH } from '../../common/constants';
import { CompanyDetailTabs } from '../../../../companyDetail';

const columnHelper = createColumnHelper<DepositLedgerTableRecord>();

export const useColumns = () =>
    useMemo(
        () => [
            columnHelper.accessor('ownerName', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.Owner} />,
                cell: (info) => {
                    const { ownerName, ownerId, ownerType, billId } = info.row.original;
                    if (!ownerName || !ownerId) {
                        return (
                            <Typography data-test-id="no-owner-name" textStyle="bodyMedium" as="span">
                                {UnicodeEntity.Dash}
                            </Typography>
                        );
                    }
                    if (ownerType === LedgerAccountType.Customer) {
                        return (
                            <EnterpriseLink
                                variant="basic"
                                to="Billing/Account/$accountId"
                                params={{ accountId: ownerId || '' }}
                                search={{ billId: billId || undefined }}
                            >
                                <TruncatedText text={ownerName} maxLength={TEXT_LENGTH} />
                            </EnterpriseLink>
                        );
                    }
                    return (
                        <EnterpriseLink
                            variant="basic"
                            to="Company/Detail/$id"
                            params={{ id: ownerId || '' }}
                            search={{ tab: CompanyDetailTabs.Billing, billId: billId || undefined }}
                        >
                            <TruncatedText text={ownerName} maxLength={TEXT_LENGTH} />
                        </EnterpriseLink>
                    );
                },
            }),
            columnHelper.accessor('associatedAccountName', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.AssociatedProfile} />,
                cell: (info) => {
                    const { associatedAccountName, associatedAccountId, associatedAccountType, billId } =
                        info.row.original;
                    if (!associatedAccountName || !associatedAccountId) {
                        return (
                            <Typography data-test-id="no-associated-name" textStyle="bodyMedium" as="span">
                                {UnicodeEntity.Dash}
                            </Typography>
                        );
                    }
                    if (associatedAccountType === LedgerAccountType.Customer) {
                        return (
                            <EnterpriseLink
                                variant="basic"
                                to="Billing/Account/$accountId"
                                params={{ accountId: associatedAccountId || '' }}
                                search={{ billId: billId || undefined }}
                            >
                                <TruncatedText text={associatedAccountName} maxLength={TEXT_LENGTH} />
                            </EnterpriseLink>
                        );
                    }
                    return (
                        <EnterpriseLink
                            variant="basic"
                            to="Company/Detail/$id"
                            params={{ id: associatedAccountId || '' }}
                            search={{ tab: CompanyDetailTabs.Billing, billId: billId || undefined }}
                        >
                            <TruncatedText text={associatedAccountName} maxLength={TEXT_LENGTH} />
                        </EnterpriseLink>
                    );
                },
            }),
            columnHelper.accessor('billName', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.Bill} />,
                cell: (info) => <BillColumn info={info} />,
            }),
            columnHelper.accessor('arrivalDate', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.ArrivalDate} />,
                cell: (info) => {
                    const arrivalDate = info.getValue();
                    return arrivalDate ? (
                        <span data-test-id="arrival-date">
                            <LocalizedDateFromUtc utcString={arrivalDate} />
                        </span>
                    ) : (
                        <span data-test-id="no-arrival-date">{UnicodeEntity.Dash}</span>
                    );
                },
            }),
            columnHelper.accessor('departureDate', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.DepartureDate} />,
                cell: (info) => {
                    const departureDate = info.getValue();
                    return departureDate ? (
                        <span data-test-id="departure-date">
                            <LocalizedDateFromUtc utcString={departureDate} />
                        </span>
                    ) : (
                        <span data-test-id="no-departure-date">{UnicodeEntity.Dash}</span>
                    );
                },
            }),
            columnHelper.accessor('reservationState', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.LedgersReservationStatus} />,
                cell: (info) => {
                    const reservationState = info.getValue();
                    return reservationState && reservationState !== 'Undefined' ? (
                        <ReservationStatusTag reservationStatus={reservationState} />
                    ) : (
                        <span data-test-id="no-reservation-status">{UnicodeEntity.Dash}</span>
                    );
                },
            }),
            columnHelper.accessor('lastPaid', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.DepositLedgerTableColumnHeaderLastPaid} />,
                cell: (info) => {
                    const lastPaid = info.getValue();
                    return lastPaid ? (
                        <span data-test-id="last-paid">
                            <LocalizedDateFromUtc utcString={lastPaid} />
                        </span>
                    ) : (
                        <span data-test-id="no-last-paid">{UnicodeEntity.Dash}</span>
                    );
                },
            }),
            columnHelper.accessor('balance', {
                enableSorting: true,
                header: () => <LocalizedText textKey={TextKey.DepositLedgerTableColumnHeaderDepositBalance} />,
                cell: (info) => {
                    const balance = info.getValue();
                    return balance ? (
                        <LocalizedCurrencyValue value={{ value: balance.value, currency: balance.currencyCode }} />
                    ) : (
                        <span data-test-id="no-balance">{UnicodeEntity.Dash}</span>
                    );
                },
            }),
        ],
        []
    );
